import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import TripService from '../services/tripService';
import DatabaseActivityService from '../services/databaseActivityService';
import FriendsServiceDB from '../services/friendsServiceDB';
import FollowingService from '../services/followingService';
import useCurrentLocation from './useCurrentLocation';
import { getLocalDateString } from '../utils/dateUtils';
import { composeTeaser } from '../utils/whatsOnTeaser';
import { composeWhatsOnResult, followFeedItems, HOME_LIMIT } from '../utils/composeWhatsOnSections';
import { activeDiscoveryCenter, explainFollow, isAwayFromHome } from '../utils/whatsOnDiscovery';
import { getDiscoverySelection } from '../services/whatsOnDiscoveryService';
import { recordWhatsOnDiagnostics } from '../utils/whatsOnDiagnostics';
import { fetchCurrentInvites } from '../utils/pendingInvites';
import { feedItemKey } from '../utils/feedItemKey';
import { buildPlans } from '../utils/homePlans';
import { INVITES_CHANGED_EVENT } from '../utils/cacheInvalidation';
import { withoutBlockedUsers } from '../utils/blockedCards';

const sectionPayload = data => Array.isArray(data) ? { items: data } : data;

// What's On teaser sizing — a glance, not the page (handoff doc §13). The
// bands request is capped server-side so Home never parses the page's full
// 200-item bands for a 12-card strip; the feed asks for a little extra so
// the strip survives dedupe against the pins.
const TEASER_FOLLOW_LIMIT = 6;
const TEASER_FEED_LIMIT = 24;
// Home's strip answers "what could I miss soon", so it stops at two weeks. The
// /whats-on page keeps the 120-day horizon and owns the long view (its Book
// Ahead band exists for exactly that). A strip whose tail was a concert in
// December was not "what's on" — it was an events list.
const TEASER_HORIZON_DAYS = 14;

const activityService = new DatabaseActivityService();

// Module-level cache so dashboard data survives page navigations
// (e.g. Home → Activity Detail → Home renders instantly within TTL).
// Key: `${userId}:${section}` → { data, ts }
//
// Stale-while-revalidate: entries are NOT evicted at the TTL. A fresh entry
// (< TTL) is used as-is. A stale entry (>= TTL) is still rendered immediately
// (no skeleton), and the caller refetches in the background to swap in fresh
// data silently. Entries are only dropped by explicit invalidation.
const dashboardCache = new Map();
const DASHBOARD_TTL_MS = 60_000;

// Returns { data, isStale } when an entry exists, or null when absent.
// `isStale` tells the caller whether to trigger a background revalidation.
const getCached = (userId, section) => {
  if (!userId) return null;
  const entry = dashboardCache.get(`${userId}:${section}`);
  if (!entry) return null;
  return { data: entry.data, isStale: Date.now() - entry.ts > DASHBOARD_TTL_MS };
};

const setCached = (userId, section, data) => {
  if (!userId) return;
  dashboardCache.set(`${userId}:${section}`, { data, ts: Date.now() });
};

// Clears all cached home-dashboard sections for a user. Exported so mutations
// in other pages (e.g. scheduling, trip save) can force a fresh dashboard
// render on the user's next return to Home.
export const invalidateDashboardCache = (userId) => {
  if (!userId) {
    dashboardCache.clear();
    return;
  }
  const prefix = `${userId}:`;
  for (const key of dashboardCache.keys()) {
    if (key.startsWith(prefix)) dashboardCache.delete(key);
  }
};

// Location-dependent sections only — the coords-change effect below uses this
// so a GPS fix doesn't needlessly evict Coming Up (which doesn't rank by
// location and would otherwise refetch on every cold Home load for users
// with "use my current location" enabled).
const FEED_SECTIONS = ['whatsAround', 'friends', 'whatsOn', 'inspiration'];

// Travel inspiration: how many ranked posts the page asks for, and how many
// about the hero trip's destination lead the list ("For your Lisbon trip").
const INSPIRATION_LIMIT = 12;
const INSPIRATION_TRIP_LIMIT = 4;
const INSPIRATION_TRIP_RADIUS_KM = 100;
const INSPIRATION_TYPES = 'trips,albums,collections,boards';
const INSPIRATION_TRIP_TYPES = 'trips,albums';
const invalidateFeedSectionsCache = (userId) => {
  if (!userId) return;
  for (const section of FEED_SECTIONS) {
    dashboardCache.delete(`${userId}:${section}`);
  }
};

/**
 * useHomeDashboard — manages data fetching and lazy loading for the dashboard Home page.
 *
 * - Plans (trips with their plan facts + the week's activities + invites):
 *   eagerly loaded on mount; src/utils/homePlans.js turns them into the hero,
 *   the rail and the "While you're in" chips
 * - What's On, What's Around, Friends, Inspiration: lazy loaded via IntersectionObserver
 *
 * The three lazy sections answer three different questions and must not overlap
 * (docs/DISCOVERY_LISTS_MODEL.md §3): What's On is dated and fenced, What's
 * Around is undated, Friends is provenance. "Trending" used to sit here too —
 * retired because it was a ranking mode rather than a question, over a
 * `trending_velocity` signal that is ~0 for almost every row.
 */
function useHomeDashboard() {
  const { t } = useTranslation('home');
  const { currentUser } = useAuth();

  // Section data states
  // `plans`: the viewer's trips (with their plan facts), the week's scheduled
  // activities and every pending invite — buildPlans (homePlans.js) picks the
  // hero and orders the rail from them.
  const [plans, setPlans] = useState({ trips: [], activities: [], invites: [], loading: true, error: null });
  const [inspiration, setInspiration] = useState({ items: [], loading: false, loaded: false, error: null });
  const [whatsAround, setWhatsAround] = useState({ items: [], loading: false, loaded: false, error: null });
  const [friends, setFriends] = useState({ items: [], loading: false, loaded: false, error: null });
  const [whatsOn, setWhatsOn] = useState({ items: [], loading: false, loaded: false, error: null });

  // Current-location coords for feed requests. Resolved by useCurrentLocation
  // (only when the user enabled "use my current location"); null → backend uses
  // stored home coords. Updates only on a meaningful (>~10km) move.
  const userCoords = useCurrentLocation();

  // Lazy loading sentinel refs
  const whatsAroundRef = useRef(null);
  const friendsRef = useRef(null);
  const whatsOnRef = useRef(null);
  const inspirationRef = useRef(null);

  // Sections with a background revalidation already in flight. The loaded/
  // loading state guard below only covers fresh loads — the revalidating path
  // renders stale data first (loaded: true), so without this an
  // IntersectionObserver double-fire would issue duplicate background fetches.
  const revalidatingKeysRef = useRef(new Set());
  const plansSeqRef = useRef(0);
  // The plans as last committed: Inspiration reads the hero trip's place from
  // here when it loads (its sentinel fires after Plans has usually landed).
  const plansRef = useRef({ trips: [], invites: [] });

  // When the detected location changes meaningfully (e.g. the user traveled),
  // drop cached feed sections and mark them unloaded so they re-rank around
  // the new location when next in view. Skips the initial render.
  //
  // The reset covers IN-FLIGHT sections too (`prev.loading`), and the
  // generation stamp below lets loadFeedSection discard a fetch that started
  // under the old centre. Without both, a coords fix landing mid-flight was
  // silently dropped: the loaded-only reset no-oped, the observer's refire hit
  // the loading guard, and the stale fetch committed + re-cached — which for
  // What's On meant a HARD 161km fence around the saved home ("What's On Near
  // You" showing Amsterdam in Tokyo) with nothing ever refetching.
  const [settingsGeneration, setSettingsGeneration] = useState(0);
  const coordsInitRef = useRef(true);
  const coordsGenRef = useRef(0);
  useEffect(() => {
    if (coordsInitRef.current) {
      coordsInitRef.current = false;
      return;
    }
    coordsGenRef.current += 1;
    invalidateFeedSectionsCache(currentUser?.id);
    const reset = prev => (prev.loaded || prev.loading
      ? { ...prev, loaded: false, loading: false }
      : prev);
    setWhatsAround(reset);
    setFriends(reset);
    setWhatsOn(reset);
    setInspiration(reset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCoords]);

  // Typed-location settings changes must replace the global cohort too.
  useEffect(() => {
    const refresh = () => {
      coordsGenRef.current += 1;
      setSettingsGeneration(value => value + 1);
      invalidateFeedSectionsCache(currentUser?.id);
      const reset = prev => ({ ...prev, loaded: false, loading: false });
      setWhatsOn(reset);
      setWhatsAround(reset);
      setFriends(reset);
      setInspiration(reset);
    };
    window.addEventListener('settingsUpdated', refresh);
    return () => window.removeEventListener('settingsUpdated', refresh);
  }, [currentUser?.id]);

  // --- Plans (eager load) ---
  // `silent`: refetch without a skeleton, over what's already on screen — for
  // an invite answered while Home is mounted.
  const loadPlans = useCallback(async ({ skipCache = false, silent = false } = {}) => {
    const userId = currentUser?.id;

    // Stale-while-revalidate: render any cached data immediately. If it's fresh,
    // we're done. If it's stale, keep showing it (no skeleton) and fall through to
    // refetch in the background, swapping in fresh data when it lands.
    let revalidating = false;
    if (!skipCache) {
      const cached = getCached(userId, 'plans');
      if (cached) {
        plansRef.current = cached.data;
        setPlans({ ...cached.data, loading: false, error: null });
        if (!cached.isStale) return;
        // Skip if a background revalidation for this section is already in flight.
        if (revalidatingKeysRef.current.has('plans')) return;
        revalidatingKeysRef.current.add('plans');
        revalidating = true;
      }
    }

    // Only the latest FETCHING load may commit: answering an invite fires a
    // refetch that can overtake one already in flight, and the older response
    // would put the answered invite back. Taken here, after the cache returns
    // above — a call that only rendered the cache must not count as newer
    // than the fetch it deferred to, or that fetch is discarded.
    const seq = ++plansSeqRef.current;
    const isLatest = () => seq === plansSeqRef.current;

    // Only show the loading state when there's nothing on screen yet — a background
    // revalidation must not flash a skeleton over already-rendered stale content.
    if (!revalidating && !silent) {
      setPlans(prev => ({ ...prev, loading: true, error: null }));
    }
    try {
      const [trips, activities, invites] = await Promise.all([
        // The upcoming lean list plus the plan facts (day counts, first empty
        // day, stay, destination, legs). homePlans.js derives each trip's
        // stage from them and drops what is past or already recapped.
        TripService.getHomePlans({ throwOnError: true }),
        activityService.getUpcomingActivities(),
        // Invites are additive — a failure here must not blank the user's own
        // plans, so it degrades to "no invites" rather than failing the section.
        // Cut to the user's own calendar inside (the server's cut is UTC and
        // a day loose) — the same list the planner's Invitations strip shows.
        fetchCurrentInvites().catch(err => {
          console.error('Error loading pending invites:', err);
          return [];
        })
      ]);

      // The server window is a day loose on each side (it only knows UTC);
      // this is the exact one — local today through a week out.
      const now = new Date();
      const todayStr = getLocalDateString(now);
      // Calendar days, not 7×24h: across a DST fall-back 168h lands at 23:00
      // the day before, dropping today+7.
      const weekOutStr = getLocalDateString(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7));
      const weekActivities = (activities || []).filter(a => {
        const d = (a.scheduled_date || a.scheduledDate || '').slice(0, 10);
        return d >= todayStr && d <= weekOutStr;
      });

      const result = { trips: trips || [], activities: weekActivities, invites };
      if (!isLatest()) return;
      plansRef.current = result;
      setPlans({ ...result, loading: false, error: null });
      setCached(userId, 'plans', result);
    } catch (err) {
      console.error('Error loading plans:', err);
      // A failed background revalidation should keep the stale content on screen
      // rather than replace it with an error — the user still has usable data.
      if (revalidating || silent || !isLatest()) return;
      setPlans({
        trips: [],
        activities: [],
        invites: [],
        loading: false,
        error: err.message || t('plans.loadError')
      });
    } finally {
      if (revalidating) revalidatingKeysRef.current.delete('plans');
    }
  }, [currentUser?.id, t]);

  useEffect(() => {
    if (currentUser) loadPlans();
    else setPlans({ trips: [], activities: [], invites: [], loading: false, error: null });
  }, [currentUser, loadPlans]);

  // An invite answered anywhere (the hero, the rail, or the notification
  // center overlaid on top of them) changes the hero and the rail.
  useEffect(() => {
    if (!currentUser) return undefined;
    const onInvitesChanged = () => loadPlans({ skipCache: true, silent: true });
    window.addEventListener(INVITES_CHANGED_EVENT, onInvitesChanged);
    return () => window.removeEventListener(INVITES_CHANGED_EVENT, onInvitesChanged);
  }, [currentUser, loadPlans]);

  // --- Feed section loaders ---
  // `fetcher` returns the section's items, throwing on failure. Sections that
  // need more than one request (What's On) compose inside their fetcher; the
  // SWR/revalidation guards here stay identical for every section.
  const loadFeedSection = useCallback(async (fetcher, setter, cacheKey, { skipCache = false } = {}) => {
    if (!cacheKey) {
      console.error('loadFeedSection called without cacheKey');
      return;
    }
    const userId = currentUser?.id;
    // Which centre this fetch belongs to. Compared before committing: a fetch
    // that outlives a coords change must not write results ranked/fenced on
    // the old centre (see the coords effect above).
    const gen = coordsGenRef.current;

    // Stale-while-revalidate: render cached data immediately. Fresh → done.
    // Stale → keep it on screen (loaded: true, no skeleton) and fall through to
    // refetch in the background, swapping in fresh results when they land.
    let revalidating = false;
    if (!skipCache) {
      const cached = getCached(userId, cacheKey);
      if (cached) {
        setter({ ...sectionPayload(cached.data), loading: false, loaded: true, error: null });
        if (!cached.isStale) return;
        // Skip if a background revalidation for this section is already in flight.
        if (revalidatingKeysRef.current.has(cacheKey)) return;
        revalidatingKeysRef.current.add(cacheKey);
        revalidating = true;
      }
    }

    if (!revalidating) {
      // Guard against double-load from IntersectionObserver firing before state propagates
      let shouldLoad = true;
      setter(prev => {
        if (prev.loaded || prev.loading) { shouldLoad = false; return prev; }
        return { ...prev, loading: true, error: null };
      });
      if (!shouldLoad) return;
    }
    try {
      const fetched = sectionPayload(await fetcher());
      // Answered before a block made while it was in flight? Its cards must
      // not reach the screen or the cache (C74).
      const payload = { ...fetched, items: withoutBlockedUsers(fetched.items) };
      const { items } = payload;
      // The centre moved while this fetch was in flight: discard. The coords
      // effect already reset this section, and the re-observed sentinel starts
      // a fresh fetch under the new generation.
      if (gen !== coordsGenRef.current) return;
      // A background revalidation that comes back EMPTY keeps what is on
      // screen. A failure keeps it too (catch below) — but an empty refresh
      // is sneakier: it would silently unmount a hide-when-empty section
      // (What's On, Friends' Activity) under a user who may be mid-scroll in
      // it. The stale cards stand until a cold load, whose fresh path commits
      // emptiness honestly.
      if (revalidating && items.length === 0) return;
      setter({ ...payload, loading: false, loaded: true, error: null });
      setCached(userId, cacheKey, payload);
    } catch (err) {
      console.error('Error loading feed section:', err);
      if (gen !== coordsGenRef.current) return;
      // A failed background revalidation keeps the stale content (and loaded
      // state) on screen instead of replacing it with an error.
      if (revalidating) return;
      setter({
        items: [],
        loading: false,
        loaded: true,
        error: err.message || t('feedSection.loadError')
      });
    } finally {
      if (revalidating) revalidatingKeysRef.current.delete(cacheKey);
    }
  }, [currentUser?.id, t]);

  const fetchFeedItems = useCallback(async (params) => {
    const result = await FriendsServiceDB.getFeed({
      ...params,
      limit: 10,
      lat: userCoords?.lat,
      lng: userCoords?.lng
    });
    // getFeed reports failure via a returned `error` field, never by throwing.
    // Surfacing it here reaches the section's error + retry branch instead of
    // caching [] as a clean "no content" — a network blip and an empty feed
    // are not the same thing, and the cache made the confusion sticky.
    if (result.error) {
      throw new Error(typeof result.error === 'string' ? result.error : t('feedSection.loadError'));
    }
    return result.feed || [];
  }, [userCoords, t]);

  // What's On teaser: the follow-derived pins plus geo-fenced nearby filler,
  // composed by composeTeaser (pins lead, filler never interleaves above).
  //
  // Serialized on purpose: the filler's fence needs a centre, and for anyone
  // not sharing device location the centre (their saved home) lives on the
  // bands response. Fencing correctly costs one extra round trip on a lazy,
  // cached strip; "near you" content that is not near costs trust. Same
  // centre rule as the page itself (WhatsOn.js).
  const fetchWhatsOnItems = useCallback(async () => {
    const bands = await FollowingService.getBands({ limit: TEASER_FOLLOW_LIMIT });
    if (bands.error) {
      throw new Error(typeof bands.error === 'string' ? bands.error : t('feedSection.loadError'));
    }
    const center = activeDiscoveryCenter(userCoords, bands.home);
    const followItems = followFeedItems(bands.newly, bands.upcoming)
      .map(item => explainFollow(item, center, bands.home));
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + TEASER_HORIZON_DAYS);
    const params = {
      center, limit: TEASER_FEED_LIMIT,
      dateStart: getLocalDateString(new Date()), dateEnd: getLocalDateString(horizon)
    };
    const result = await getDiscoverySelection({
      ...params, target: HOME_LIMIT,
      countVisible: (ranked, farther) => composeTeaser({ followItems, ranked, farther }).length
    });
    if (result.error) throw new Error(t('feedSection.loadError'));
    const composition = composeWhatsOnResult({ home: true, followItems, ranked: result.primary, farther: result.farther });
    const items = composition.sections.flatMap(section => section.items);
    recordWhatsOnDiagnostics({ surface: 'home', centerKind: center.kind, counts: result.counts,
      followCandidates: followItems.length, compositionCounts: composition.counts, items });
    return {
      items, centerKind: center.kind,
      // Where the fence is, so the section's title names that place and not
      // home when the phone is somewhere else (on a trip).
      awayFromHome: isAwayFromHome(center, bands.home),
      center: center.kind === 'none' ? null : { lat: center.lat, lng: center.lng },
      followTruncated: !!(bands.newly?.truncated || bands.upcoming?.truncated)
    };
  }, [userCoords, t]);

  // A feed request centred where the CALLER says, not where the viewer is:
  // the trip-anchored lists ("For your Lisbon trip") pass their own centre.
  const fetchAnchoredFeed = useCallback(async (params) => {
    const result = await FriendsServiceDB.getFeed(params);
    if (result.error) {
      throw new Error(typeof result.error === 'string' ? result.error : t('feedSection.loadError'));
    }
    return result.feed || [];
  }, [t]);

  // Travel inspiration: people's posts (trips, albums, collections, boards)
  // ranked for the viewer, with posts ABOUT the hero trip's destination first
  // while that trip is in the works. The friends strip is folded in by the
  // page (its posts lead the cards; its visits become the rows beneath).
  const fetchInspirationItems = useCallback(async () => {
    const { hero, away } = buildPlans({ trips: plansRef.current.trips, invites: plansRef.current.invites });
    const anchor = (hero.trip && away.find(a => a.trip.id === hero.trip.id)) || away[0] || null;
    const [ranked, forTrip] = await Promise.all([
      fetchAnchoredFeed({
        sort: 'ranked', contentType: 'selected', contentTypes: INSPIRATION_TYPES,
        limit: INSPIRATION_LIMIT, lat: userCoords?.lat, lng: userCoords?.lng
      }),
      anchor
        ? fetchAnchoredFeed({
          sort: 'ranked', contentType: 'selected', contentTypes: INSPIRATION_TRIP_TYPES,
          limit: INSPIRATION_TRIP_LIMIT, lat: anchor.center.lat, lng: anchor.center.lng,
          radiusKm: INSPIRATION_TRIP_RADIUS_KM
        }).catch(() => [])
        : Promise.resolve([])
    ]);
    const tagged = forTrip.map(item => ({ ...item, _forTrip: { id: anchor.trip.id, name: anchor.center.name } }));
    const seen = new Set();
    const items = [];
    for (const item of [...tagged, ...ranked]) {
      // A post about your own trip is not inspiration for it.
      if (item.is_own_content) continue;
      const key = feedItemKey(item);
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      items.push(item);
    }
    return items;
  }, [fetchAnchoredFeed, userCoords]);

  // contentType 'undated' is the boundary with What's On: anything with a date
  // you could miss belongs there and must not be repeated here.
  const loadWhatsAround = useCallback(() => {
    loadFeedSection(() => fetchFeedItems({ sort: 'ranked', contentType: 'undated' }), setWhatsAround, 'whatsAround');
  }, [loadFeedSection, fetchFeedItems]);

  const loadFriends = useCallback(() => {
    loadFeedSection(() => fetchFeedItems({ scope: 'following', sort: 'ranked' }), setFriends, 'friends');
  }, [loadFeedSection, fetchFeedItems]);

  const loadWhatsOn = useCallback(() => {
    loadFeedSection(fetchWhatsOnItems, setWhatsOn, 'whatsOn');
  }, [loadFeedSection, fetchWhatsOnItems]);

  const loadInspiration = useCallback(() => {
    loadFeedSection(fetchInspirationItems, setInspiration, 'inspiration');
  }, [loadFeedSection, fetchInspirationItems]);

  // Retry helpers — bypass cache and clear `loaded` so loadFeedSection runs
  // again. The cache entry is DELETED first: flipping `loaded` re-runs the
  // observer effect, which re-observes the in-view sentinel and fires a second
  // load WITHOUT skipCache — with the entry gone that second call falls
  // through to the `prev.loading` guard instead of repainting the pre-retry
  // payload (and, past the TTL, spawning a duplicate background refetch).
  const dropCached = useCallback((key) => {
    if (currentUser?.id) dashboardCache.delete(`${currentUser.id}:${key}`);
  }, [currentUser?.id]);

  const retryWhatsAround = useCallback(() => {
    dropCached('whatsAround');
    setWhatsAround(prev => ({ ...prev, loaded: false, error: null }));
    loadFeedSection(() => fetchFeedItems({ sort: 'ranked', contentType: 'undated' }), setWhatsAround, 'whatsAround', { skipCache: true });
  }, [loadFeedSection, fetchFeedItems, dropCached]);

  const retryFriends = useCallback(() => {
    dropCached('friends');
    setFriends(prev => ({ ...prev, loaded: false, error: null }));
    loadFeedSection(() => fetchFeedItems({ scope: 'following', sort: 'ranked' }), setFriends, 'friends', { skipCache: true });
  }, [loadFeedSection, fetchFeedItems, dropCached]);

  const retryWhatsOn = useCallback(() => {
    dropCached('whatsOn');
    setWhatsOn(prev => ({ ...prev, loaded: false, error: null }));
    loadFeedSection(fetchWhatsOnItems, setWhatsOn, 'whatsOn', { skipCache: true });
  }, [loadFeedSection, fetchWhatsOnItems, dropCached]);

  const retryInspiration = useCallback(() => {
    dropCached('inspiration');
    setInspiration(prev => ({ ...prev, loaded: false, error: null }));
    loadFeedSection(fetchInspirationItems, setInspiration, 'inspiration', { skipCache: true });
  }, [loadFeedSection, fetchInspirationItems, dropCached]);

  // --- IntersectionObserver for lazy sections ---
  useEffect(() => {
    const entries = [
      { ref: whatsOnRef, loaded: whatsOn.loaded, load: loadWhatsOn, id: 'whats-on' },
      { ref: whatsAroundRef, loaded: whatsAround.loaded, load: loadWhatsAround, id: 'whats-around' },
      { ref: friendsRef, loaded: friends.loaded, load: loadFriends, id: 'friends' },
      // Inspiration waits for the plans: its fetcher reads the hero trip's
      // place from plansRef, and a tall viewport can bring its sentinel into
      // range before the plans land. "Loaded" while plans load means "not
      // observed"; when plans.loading flips the effect re-runs and observes.
      { ref: inspirationRef, loaded: inspiration.loaded || plans.loading, load: loadInspiration, id: 'inspiration' }
    ];

    const observer = new IntersectionObserver(
      (observedEntries) => {
        observedEntries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const section = entries.find(e => e.ref.current === entry.target);
          if (section && !section.loaded) {
            section.load();
          }
        });
      },
      { rootMargin: '200px' }
    );

    entries.forEach(e => {
      if (e.ref.current && !e.loaded) observer.observe(e.ref.current);
    });

    return () => observer.disconnect();
  }, [whatsAround.loaded, friends.loaded, whatsOn.loaded, inspiration.loaded, plans.loading, loadWhatsAround, loadFriends, loadWhatsOn, loadInspiration, settingsGeneration]);

  // Update items within a section (e.g. toggling like state). What's On is
  // included: composeTeaser returns raw feed-row-shaped items, so the same
  // updater applies to all three strips.
  const updateSectionItems = useCallback((updater) => {
    const applyToSection = (setter) => {
      setter(prev => ({
        ...prev,
        items: updater(prev.items)
      }));
    };
    applyToSection(setWhatsAround);
    applyToSection(setFriends);
    applyToSection(setWhatsOn);
    applyToSection(setInspiration);
  }, []);

  const retryPlans = useCallback(() => loadPlans({ skipCache: true }), [loadPlans]);
  // After a write that changed the plans (a stop added from Home): fresh data
  // over what's on screen, no skeleton, and a failed refetch keeps the page.
  const refreshPlans = useCallback(() => loadPlans({ skipCache: true, silent: true }), [loadPlans]);

  return {
    plans,
    retryPlans,
    refreshPlans,
    inspiration,
    inspirationRef,
    retryInspiration,
    whatsAround,
    whatsAroundRef,
    retryWhatsAround,
    friends,
    friendsRef,
    retryFriends,
    whatsOn,
    whatsOnRef,
    retryWhatsOn,
    // The hero's home-city variant wants the weekend before the strip scrolls
    // into view; same guarded loader the sentinel fires.
    loadWhatsOn,
    updateSectionItems
  };
}

export default useHomeDashboard;
